/**
 * Team-split view logic: which team owns each side of the pitch right now,
 * and the completed passes derived from the pass-potential moments.
 *
 * A completed pass is inferred, not recorded: the pipeline's
 * pass_potential.parquet carries the BALL HOLDER at sampled moments, so two
 * consecutive moments where the same team holds through a different jersey
 * are read as "the ball went from A to B". Moments far apart in time are
 * not chained — a long gap usually spans a dead ball, and drawing an arrow
 * across it would invent a pass nobody played. All positions are METRES
 * from the centre spot (pass-potential units, unlike tracking's cm).
 */
import { SHORT_LABELS } from '../../lib/pitchZones'
import type { PassPotentialData } from './canvasController'

export interface CompletedPass {
  /** The frame the receiver was first seen holding. */
  frame: number
  team: number
  fromJersey: number
  toJersey: number
  fromX: number
  fromY: number
  toX: number
  toY: number
  /** +1 attacking toward +x, -1 toward -x, from the receiving moment's own
   *  attacking_direction. Undefined when the moment did not say — a guessed
   *  sign would flip every "into the box" reading for that half. */
  attackSign?: 1 | -1
}

function signOf(dir: string | undefined): 1 | -1 | undefined {
  return dir === 'right' ? 1 : dir === 'left' ? -1 : undefined
}

/** Consecutive holders further apart than this are not chained (15s @30fps
 *  — spans a throw-in or a stoppage, not a pass). */
export const MAX_PASS_GAP_FRAMES = 450

export function deriveCompletedPasses(
  byFrame: Record<number, PassPotentialData>,
): CompletedPass[] {
  const frames = Object.keys(byFrame).map(Number).sort((a, b) => a - b)
  const out: CompletedPass[] = []
  let prev: { frame: number; team: number; jersey: number; x: number; y: number } | null = null
  for (const f of frames) {
    const m = byFrame[f]
    const h = m?.ball_holder
    if (!m || !h) continue
    const team = m.team_id ?? h.team
    if (prev && prev.team === team && prev.jersey !== h.jersey
        && f - prev.frame <= MAX_PASS_GAP_FRAMES) {
      out.push({
        frame: f, team,
        fromJersey: prev.jersey, toJersey: h.jersey,
        fromX: prev.x, fromY: prev.y, toX: h.x, toY: h.y,
        attackSign: signOf(m.attacking_direction),
      })
    }
    prev = { frame: f, team, jersey: h.jersey, x: h.x, y: h.y }
  }
  return out
}

export interface TeamSides { left: number; right: number }

/**
 * Which tracking team index owns each side of the video right now; home is
 * homeIndex (lib/homeIndex homeIndexOf). The "left team" is the one whose
 * GOAL is on the left — the team attacking right — which flips at halftime.
 *
 * Null when the direction is not recorded (anything but 'left' or 'right'):
 * callers show that it is not recorded rather than place teams on a guessed
 * side (ledger 26, RULED B — never a silent 'right').
 */
export function sidesAtFrame(
  homeStartDirection: string | null | undefined,
  phase: number,
  homeIndex: 0 | 1,
): TeamSides | null {
  // home_team_start_direction is the direction HOME attacks in the first half.
  if (homeStartDirection !== 'left' && homeStartDirection !== 'right') return null
  const homeAttacksRight = homeStartDirection === 'right'
  const homeAttacksRightNow = phase === 2 ? !homeAttacksRight : homeAttacksRight
  // Attacking right = defending the left goal = the left team.
  const home = homeIndex
  const away = 1 - homeIndex
  return homeAttacksRightNow ? { left: home, right: away }
                             : { left: away, right: home }
}

export interface NetworkNode {
  jersey: number
  x: number
  y: number
  touches: number
}

export interface NetworkEdge {
  from: number
  to: number
  n: number
}

/**
 * The aggregated pass network up to a frame: one node per jersey at the
 * mean of the positions it held the ball at (metres), one DIRECTED edge
 * per passer->receiver pair with its count. This is the "numbers" view —
 * who combines with whom — beside the recency view of the pass trail.
 */
export function aggregateNetwork(
  passes: CompletedPass[], team: number, uptoFrame: number,
): { nodes: NetworkNode[]; edges: NetworkEdge[]; total: number } {
  const acc = new Map<number, { sx: number; sy: number; k: number }>()
  const seat = (jersey: number, x: number, y: number) => {
    const cur = acc.get(jersey) ?? { sx: 0, sy: 0, k: 0 }
    cur.sx += x; cur.sy += y; cur.k += 1
    acc.set(jersey, cur)
  }
  const edgeMap = new Map<string, NetworkEdge>()
  let total = 0
  for (const p of passes) {
    if (p.team !== team || p.frame > uptoFrame) continue
    seat(p.fromJersey, p.fromX, p.fromY)
    seat(p.toJersey, p.toX, p.toY)
    const key = `${p.fromJersey}>${p.toJersey}`
    const e = edgeMap.get(key) ?? { from: p.fromJersey, to: p.toJersey, n: 0 }
    e.n += 1
    edgeMap.set(key, e)
    total += 1
  }
  const nodes = [...acc.entries()].map(([jersey, v]) => ({
    jersey, x: v.sx / v.k, y: v.sy / v.k, touches: v.k,
  }))
  return { nodes, edges: [...edgeMap.values()], total }
}

/** The penalty area on the NOMINAL 105x68 pitch every team-split panel
 *  draws (metres, centre origin): 16.5m deep, 40.32m wide. */
export const BOX_DEPTH_M = 16.5
export const BOX_HALF_WIDTH_M = 20.16
const BOX_FRONT_X = 52.5 - BOX_DEPTH_M

/**
 * Passes INTO the opponent's box up to a frame: received inside it, played
 * from outside it. Returned ORIENTED — the team attacks +x — so a panel can
 * draw every half on one end. Passes whose direction is unknown are left
 * out rather than placed on a guessed end.
 */
export function passesIntoBox(
  passes: CompletedPass[], team: number, uptoFrame: number,
): CompletedPass[] {
  const inBox = (x: number, y: number) =>
    x >= BOX_FRONT_X && Math.abs(y) <= BOX_HALF_WIDTH_M
  const out: CompletedPass[] = []
  for (const p of passes) {
    if (p.team !== team || p.frame > uptoFrame || !p.attackSign) continue
    // Ends swap at half-time: a 180-degree turn, so y flips with x.
    const s = p.attackSign
    const o = { ...p, fromX: p.fromX * s, fromY: p.fromY * s,
                toX: p.toX * s, toY: p.toY * s, attackSign: 1 as const }
    if (inBox(o.toX, o.toY) && !inBox(o.fromX, o.fromY)) out.push(o)
  }
  return out
}

/** The pipeline's chain zones, own goal to theirs — attack-relative
 *  thirds of the pitch, the middle third cut at halfway (zone_for_x_att /
 *  possession_chains_parquet). */
export const CHAIN_ZONES = [
  SHORT_LABELS.DEF, SHORT_LABELS.MFD, SHORT_LABELS.MFA, SHORT_LABELS.ATT,
] as const
export type ChainZone = typeof CHAIN_ZONES[number]

export interface BuildUpRow {
  start: ChainZone
  /** Possessions won or started in this zone. */
  n: number
  /** How many of them ended in each zone, CHAIN_ZONES order. */
  ended: [number, number, number, number]
  boxEntries: number
}

interface ChainLike {
  team_id: number
  start_frame: number
  start_zone: string | null
  end_zone: string | null
  box_entry: boolean
}

/**
 * Build-up patterns: where a team's possessions began and where they got
 * to, up to a frame. Chains without a recognised zone are skipped, not
 * binned somewhere plausible.
 */
export function buildUpProfile(
  chains: ChainLike[], team: number, uptoFrame: number,
): BuildUpRow[] {
  const rows: BuildUpRow[] = CHAIN_ZONES.map((start) => ({
    start, n: 0, ended: [0, 0, 0, 0], boxEntries: 0,
  }))
  for (const c of chains) {
    if (c.team_id !== team || c.start_frame > uptoFrame) continue
    const si = CHAIN_ZONES.indexOf(c.start_zone as ChainZone)
    const ei = CHAIN_ZONES.indexOf(c.end_zone as ChainZone)
    if (si < 0 || ei < 0) continue
    const row = rows[si]!
    row.n += 1
    row.ended[ei] = (row.ended[ei] ?? 0) + 1
    if (c.box_entry) row.boxEntries += 1
  }
  return rows
}

/**
 * Attacking sequences: possessions that reached the final third or
 * entered the box, up to a frame, most recent first.
 */
export function attackingSequences<C extends ChainLike>(
  chains: C[], team: number, uptoFrame: number,
): C[] {
  return chains
    .filter((c) => c.team_id === team && c.start_frame <= uptoFrame
      && (c.box_entry || c.end_zone === 'attacking'))
    .sort((a, b) => b.start_frame - a.start_frame)
}
